import React, { useState, useCallback } from 'react';
import { RouteComponentProps } from '@reach/router';
import { WithFormikConfig, FormikProps, withFormik } from 'formik';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { Text, Button, Icon, LinkButton } from '@el8/vital';

import { Box } from 'components';
import { notify } from 'utils';

import RootContainer from '../shared/RootContainer';
import PaymentsHeader from '../shared/PaymentsHeader';
import PaymentMethodForm, {
  PaymentMethodFormValues,
  paymentMethodValidationSchema,
  paymentMethodInitialValues,
} from '../shared/PaymentMethodForm';
import { useSaveCard } from '../shared/helpers';

type Values = PaymentMethodFormValues;

type NonFormikProps = RouteComponentProps<{
  patientId: string;
}>;

type Props = NonFormikProps & FormikProps<Values>;

const withFormikConfig: WithFormikConfig<NonFormikProps, Values> = {
  mapPropsToValues() {
    return { ...paymentMethodInitialValues };
  },
  handleSubmit() {
    // Required by Typescript, but we can't actually perform the submission here because
    // we can't access `stripe` (from useStripe) or `elements` (from useElements) inside of
    // this function. So instead, we handle submission inside the component.
  },
  validationSchema: paymentMethodValidationSchema,
};

const CardDetailsPage: React.FC<Props> = ({ isSubmitting, location, patientId }) => {
  const { patientName, practiceDisplayName } = window.elate;

  const [paymentMethod, setPaymentMethod] = useState(window.elate.paymentMethod);
  const [isEditing, setIsEditing] = useState(!paymentMethod);

  const handleEditStart = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const stripe = useStripe();
  const elements = useElements();
  const isStripeInitialized = Boolean(stripe && elements);

  const key = new URLSearchParams(location.search).get('key');

  const handleSave = useSaveCard(Number(patientId), key, (newPaymentMethod) => {
    notify('Payment method saved.');
    setPaymentMethod(newPaymentMethod);
    setIsEditing(false);
  });

  return (
    <RootContainer>
      <PaymentsHeader />
      <Text appearance="system" tag="div" style={{ marginBottom: 12 }}>
        {paymentMethod ? 'View and manage' : 'Add'} your payment information for charges related to{' '}
        {patientName} for {practiceDisplayName}.
      </Text>

      {paymentMethod && (
        <>
          <Text tag="div" textSize="large" weight="medium">
            Payment method
          </Text>
          <Box direction="row" style={isEditing ? { marginBottom: 24 } : undefined}>
            <Icon icon="credit-card" style={{ marginRight: 8, marginTop: 2 }} />
            <Text tag="div" textSize="large" style={{ flex: 1 }}>
              {paymentMethod.payment_method_description}
            </Text>
            <LinkButton
              disabled={isSubmitting}
              onClick={isEditing ? handleEditCancel : handleEditStart}
              text={isEditing ? 'Cancel update' : 'Update card'}
            />
          </Box>
        </>
      )}

      {isEditing && (
        <>
          <PaymentMethodForm />
          <Button
            disabled={!isStripeInitialized}
            kind="primary"
            loading={isSubmitting}
            onClick={handleSave}
            text="Save"
            style={{ marginBottom: 8, width: '100%' }}
          />
          <Text align="center" appearance="system" tag="div">
            By saving, you are agreeing to keep this card on file for {patientName} with{' '}
            {practiceDisplayName} (you will always be notified before any charges).
          </Text>
        </>
      )}
    </RootContainer>
  );
};

export default withFormik(withFormikConfig)(CardDetailsPage);
